import { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, Button, message, Flex } from "antd";
import { UpdateUserFormValues, User } from "@/types/user";
import { SelectOption } from "@/types/select";
import { useGetRoles, useUpdateUser } from "../../hooks/useUser";
import { capitalize } from "@/utils/text";

interface EditModalProps {
  isOpen: boolean;
  data?: User;
  onClose: () => void;
  onRefetch: () => void;
}

const EditModal = ({ isOpen, data, onClose, onRefetch }: EditModalProps) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const { mutate: updateUser } = useUpdateUser();
  const { data: roles } = useGetRoles({
    params: {
      limit: -1,
    },
  });

  const roleOptions: SelectOption[] = useMemo(() => {
    return (
      roles?.map((role) => ({
        label: capitalize(role.name),
        value: role.id.toString(),
      })) || []
    );
  }, [roles]);

  const handleSubmit = async (values: UpdateUserFormValues) => {
    setSubmitting(true);
    updateUser(
      { ...values, id: data?.id || "" },
      {
        onSuccess: () => {
          form.resetFields();
          onClose();
          message.success("Successfully updated user");
          onRefetch();
          setSubmitting(false);
        },
        onError: () => {
          setSubmitting(false);
        },
      }
    );
  };

  useEffect(() => {
    if (isOpen && data) {
      form.setFieldsValue({
        id: data.id,
        username: data.username,
        email: data.email,
        role_ids: data.roles.map((role) => role.id.toString()),
        is_active: data.is_active,
      });
    }
  }, [isOpen, data, form]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title="Edit User"
      footer={null}
      centered
      destroyOnHidden
    >
      <Form<UpdateUserFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="username"
          label="Username"
          rules={[{ required: true, message: "Please enter the username" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Please enter the email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="role_ids"
          label="Roles"
          rules={[
            { required: true, message: "Please select at least one role" },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select roles"
            options={roleOptions}
          />
        </Form.Item>

        <Flex justify="flex-end" gap={16} align="center">
          <Button onClick={onClose} variant="filled">
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            loading={submitting}
            variant="solid"
            color="primary"
          >
            Update
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default EditModal;
